import React, { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Heart } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { api } from "../lib/api";

const emptyForm = {
  a_first_name: "", a_last_name: "", a_email: "", a_phone: "",
  a_nationality: "", a_current_country: "", a_date_of_birth: "",
  b_first_name: "", b_last_name: "", b_email: "",
  b_nationality: "", b_current_country: "", b_date_of_birth: "",
  relationship_status: "common_law",
  relationship_start_date: "", cohabitation_start_date: "",
  children: false, number_of_children: 0,
  sponsorship_type: "undecided", target_pathway: "PR",
  documents_ready: [],
  notes: "", consent: true,
};

const inp = "w-full h-11 px-3 rounded-lg border border-navy/15 bg-white focus:border-terracotta focus:outline-none text-sm text-navy";
const area = "w-full px-3 py-2 rounded-lg border border-navy/15 bg-white focus:border-terracotta focus:outline-none text-sm text-navy resize-none";

const Field = ({ label, children }) => (
  <div>
    <label className="text-xs tracking-[0.2em] uppercase text-navy/50 block mb-1.5">{label}</label>
    {children}
  </div>
);

export default function CouplesRegistry() {
  const { t, lang } = useLang();
  const f = (k) => t(`registry.fields.${k}`);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const toggleDoc = (d) => {
    setForm((p) => ({
      ...p,
      documents_ready: p.documents_ready.includes(d)
        ? p.documents_ready.filter((x) => x !== d)
        : [...p.documents_ready, d],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) return;
    setBusy(true);
    try {
      const payload = {
        ...form,
        number_of_children: Number(form.number_of_children) || 0,
        language: lang,
      };
      // Coerce empty optional email to null so Pydantic EmailStr does not 422
      if (!payload.b_email) payload.b_email = null;
      await api.post("/registry/couples", payload);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const raw = err.response?.data?.detail;
      const msg = typeof raw === "string" ? raw : t("common.error");
      alert(msg);
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="bg-alabaster min-h-[80vh]" data-testid="couples-registry-done">
        <section className="max-w-[900px] mx-auto px-6 lg:px-10 py-24 text-center">
          <Heart size={64} className="mx-auto text-terracotta mb-6" fill="currentColor" />
          <h1 className="font-serif text-5xl lg:text-6xl text-navy tracking-tight mb-6">{t("registry.done_title")}</h1>
          <p className="text-navy/70 text-lg max-w-lg mx-auto mb-10">{t("registry.done_couples")}</p>
          <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-navy text-alabaster text-sm hover:bg-terracotta transition">
            {t("common.back")} <ArrowRight size={14} />
          </Link>
        </section>
      </div>
    );
  }

  const docs = [
    { k: "joint_lease", label: f("doc_joint_lease") },
    { k: "joint_bank", label: f("doc_joint_bank") },
    { k: "joint_insurance", label: f("doc_joint_insurance") },
    { k: "photos", label: f("doc_photos") },
    { k: "communication", label: f("doc_communication") },
  ];

  return (
    <div className="bg-alabaster" data-testid="couples-registry-page">
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-20 pb-14 grid lg:grid-cols-12 gap-10 items-end">
        <div className="lg:col-span-8">
          <p className="text-[10px] tracking-[0.3em] uppercase text-terracotta mb-4 inline-flex items-center gap-2">
            <Heart size={14} /> {t("registry.overline_couples")}
          </p>
          <h1 className="font-serif text-5xl lg:text-7xl text-navy tracking-tight leading-[0.95]">
            {t("registry.title_couples")}
          </h1>
        </div>
        <div className="lg:col-span-4">
          <p className="text-navy/70 leading-relaxed">{t("registry.sub_couples")}</p>
          <div className="mt-4 inline-flex items-center gap-2 text-xs text-terracotta bg-cream px-3 py-1.5 rounded-full">
            <ShieldCheck size={12} /> {t("registry.privacy")}
          </div>
          <Link to="/registry" className="mt-6 block text-sm text-terracotta link-underline" data-testid="couples-switch-talent">
            {t("registry.switch_talent")}
          </Link>
        </div>
      </section>

      <section className="max-w-[1100px] mx-auto px-6 lg:px-10 pb-24">
        <motion.form
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          onSubmit={submit}
          className="bg-white border border-navy/8 rounded-[2rem] p-8 lg:p-12 space-y-10 shadow-[0_20px_60px_rgba(6,22,42,.07)]"
          data-testid="couples-registry-form"
        >
          {/* Partner A */}
          <div>
            <p className="text-xs tracking-[0.25em] uppercase text-terracotta mb-6">01 — {f("partner_a")}</p>
            <div className="grid md:grid-cols-2 gap-5">
              <Field label={f("first_name")}><input required value={form.a_first_name} onChange={(e) => set("a_first_name", e.target.value)} className={inp} data-testid="cr-a-first" /></Field>
              <Field label={f("last_name")}><input required value={form.a_last_name} onChange={(e) => set("a_last_name", e.target.value)} className={inp} data-testid="cr-a-last" /></Field>
              <Field label={f("email")}><input required type="email" value={form.a_email} onChange={(e) => set("a_email", e.target.value)} className={inp} data-testid="cr-a-email" /></Field>
              <Field label={f("phone")}><input value={form.a_phone} onChange={(e) => set("a_phone", e.target.value)} className={inp} data-testid="cr-a-phone" /></Field>
              <Field label={f("nationality")}><input required value={form.a_nationality} onChange={(e) => set("a_nationality", e.target.value)} className={inp} data-testid="cr-a-nat" /></Field>
              <Field label={f("country_of_residence")}><input required value={form.a_current_country} onChange={(e) => set("a_current_country", e.target.value)} className={inp} data-testid="cr-a-country" /></Field>
              <Field label={f("date_of_birth")}><input type="date" value={form.a_date_of_birth} onChange={(e) => set("a_date_of_birth", e.target.value)} className={inp} data-testid="cr-a-dob" /></Field>
            </div>
          </div>

          {/* Partner B */}
          <div>
            <p className="text-xs tracking-[0.25em] uppercase text-terracotta mb-6">02 — {f("partner_b")}</p>
            <div className="grid md:grid-cols-2 gap-5">
              <Field label={f("first_name")}><input required value={form.b_first_name} onChange={(e) => set("b_first_name", e.target.value)} className={inp} data-testid="cr-b-first" /></Field>
              <Field label={f("last_name")}><input required value={form.b_last_name} onChange={(e) => set("b_last_name", e.target.value)} className={inp} data-testid="cr-b-last" /></Field>
              <Field label={f("email")}><input type="email" value={form.b_email} onChange={(e) => set("b_email", e.target.value)} className={inp} data-testid="cr-b-email" /></Field>
              <Field label={f("nationality")}><input required value={form.b_nationality} onChange={(e) => set("b_nationality", e.target.value)} className={inp} data-testid="cr-b-nat" /></Field>
              <Field label={f("country_of_residence")}><input required value={form.b_current_country} onChange={(e) => set("b_current_country", e.target.value)} className={inp} data-testid="cr-b-country" /></Field>
              <Field label={f("date_of_birth")}><input type="date" value={form.b_date_of_birth} onChange={(e) => set("b_date_of_birth", e.target.value)} className={inp} data-testid="cr-b-dob" /></Field>
            </div>
          </div>

          {/* Relationship */}
          <div>
            <p className="text-xs tracking-[0.25em] uppercase text-terracotta mb-6">03 — {lang === "fr" ? "Votre relation" : "Your relationship"}</p>
            <div className="grid md:grid-cols-2 gap-5">
              <Field label={f("relationship_status")}>
                <select value={form.relationship_status} onChange={(e) => set("relationship_status", e.target.value)} className={inp} data-testid="cr-status">
                  <option value="common_law">{f("rel_common_law")}</option>
                  <option value="married">{f("rel_married")}</option>
                  <option value="engaged">{f("rel_engaged")}</option>
                </select>
              </Field>
              <Field label={f("relationship_start_date")}><input type="date" value={form.relationship_start_date} onChange={(e) => set("relationship_start_date", e.target.value)} className={inp} data-testid="cr-rel-start" /></Field>
              <Field label={f("cohabitation_start_date")}><input type="date" value={form.cohabitation_start_date} onChange={(e) => set("cohabitation_start_date", e.target.value)} className={inp} data-testid="cr-cohab-start" /></Field>
              <Field label={f("children")}>
                <div className="flex gap-2">
                  <button type="button" onClick={() => set("children", true)} className={`flex-1 h-11 rounded-lg border text-sm ${form.children ? "bg-navy text-alabaster border-navy" : "bg-white border-navy/15 text-navy hover:border-terracotta"}`} data-testid="cr-children-yes">{f("yes")}</button>
                  <button type="button" onClick={() => { set("children", false); set("number_of_children", 0); }} className={`flex-1 h-11 rounded-lg border text-sm ${!form.children ? "bg-navy text-alabaster border-navy" : "bg-white border-navy/15 text-navy hover:border-terracotta"}`} data-testid="cr-children-no">{f("no")}</button>
                </div>
              </Field>
              {form.children && (
                <Field label={f("number_of_children")}>
                  <input type="number" min="1" max="20" value={form.number_of_children} onChange={(e) => set("number_of_children", e.target.value)} className={inp} data-testid="cr-num-children" />
                </Field>
              )}
            </div>
          </div>

          {/* Sponsorship */}
          <div>
            <p className="text-xs tracking-[0.25em] uppercase text-terracotta mb-6">04 — {lang === "fr" ? "Parrainage" : "Sponsorship"}</p>
            <div className="grid md:grid-cols-2 gap-5">
              <Field label={f("sponsorship_type")}>
                <select value={form.sponsorship_type} onChange={(e) => set("sponsorship_type", e.target.value)} className={inp} data-testid="cr-sponsorship">
                  <option value="inland">{f("sp_inland")}</option>
                  <option value="outland">{f("sp_outland")}</option>
                  <option value="undecided">{f("sp_undecided")}</option>
                </select>
              </Field>
              <Field label={f("target_pathway")}>
                <select value={form.target_pathway} onChange={(e) => set("target_pathway", e.target.value)} className={inp} data-testid="cr-pathway">
                  <option value="PR">{f("pathway_pr")}</option>
                  <option value="Study">{f("pathway_study")}</option>
                  <option value="Work">{f("pathway_work")}</option>
                  <option value="Other">{f("pathway_other")}</option>
                </select>
              </Field>
              <div className="md:col-span-2">
                <Field label={f("documents_ready")}>
                  <div className="flex flex-wrap gap-2 pt-1">
                    {docs.map((d) => (
                      <button
                        key={d.k}
                        type="button"
                        onClick={() => toggleDoc(d.k)}
                        data-testid={`cr-doc-${d.k}`}
                        className={`px-4 py-2 rounded-full border text-sm transition ${
                          form.documents_ready.includes(d.k)
                            ? "bg-terracotta text-alabaster border-terracotta"
                            : "bg-white text-navy border-navy/15 hover:border-terracotta"
                        }`}
                      >
                        {d.label}
                      </button>
                    ))}
                  </div>
                </Field>
              </div>
              <div className="md:col-span-2">
                <Field label={f("notes")}>
                  <textarea rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)} className={area} data-testid="cr-notes" />
                </Field>
              </div>
            </div>
          </div>

          {/* Consent */}
          <div className="pt-6 border-t border-navy/10">
            <label className="flex items-start gap-3 text-sm text-navy/75 cursor-pointer">
              <input type="checkbox" checked={form.consent} onChange={(e) => set("consent", e.target.checked)} className="mt-1" data-testid="cr-consent" />
              <span>{t("registry.consent")}</span>
            </label>
            <button
              type="submit"
              disabled={busy || !form.consent}
              data-testid="cr-submit"
              className="mt-6 w-full h-12 rounded-full bg-navy text-alabaster hover:bg-terracotta transition text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {busy ? t("registry.submitting") : t("registry.submit")}
              <ShieldCheck size={14} />
            </button>
            <p className="mt-4 text-xs text-navy/50 flex items-center gap-2 justify-center">
              <ShieldCheck size={12} /> {t("registry.privacy")}
            </p>
          </div>
        </motion.form>
      </section>
    </div>
  );
}
